import DetailDrawer from "../DetailDrawer";
import "./SessionsView.css";

const formatAccuracyValue = (value) => {
  // Guard null/"" first: Number(null) === 0 would render a missing reading as "0.0 m".
  if (value == null || value === "") return null;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) return null;
  if (numericValue >= 1000) {
    const kmValue =
      numericValue >= 100000 ? Math.round(numericValue / 1000) : (numericValue / 1000).toFixed(1);
    return `${kmValue} km`;
  }
  if (numericValue >= 100) return `${Math.round(numericValue)} m`;
  return `${numericValue.toFixed(1)} m`;
};

const getAccuracyLevel = (value) => {
  // Guard null/"" first: Number(null) === 0 would mislabel a missing reading as "high".
  if (value == null || value === "") return null;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) return null;
  if (numericValue <= 20) return "high";
  if (numericValue <= 100) return "medium";
  return "low";
};

const formatDistanceSummary = (value) => {
  // Guard null/"" first: Number(null) === 0 would render a missing distance as "0 m away".
  if (value == null || value === "") return null;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) return null;
  if (numericValue >= 1000) return `${Math.round(numericValue / 1000)} km away`;
  return `${Math.round(numericValue)} m away`;
};

const getEffectiveLocationMatchStatus = (session) => {
  const distanceMeters =
    session?.distance_from_outlet_meters != null
      ? Number.parseInt(session.distance_from_outlet_meters, 10)
      : null;
  const appliedRadiusMeters =
    session?.applied_radius_meters != null
      ? Number.parseInt(session.applied_radius_meters, 10)
      : null;
  const savedStatus = String(session?.location_match_status || "").trim().toLowerCase();

  if (
    Number.isInteger(distanceMeters) &&
    distanceMeters >= 0 &&
    Number.isInteger(appliedRadiusMeters) &&
    appliedRadiusMeters > 0
  ) {
    return distanceMeters <= appliedRadiusMeters ? "on_site" : "off_site";
  }

  return savedStatus || "unverified";
};

export default function SessionAuditDrawer({
  isOpen,
  onClose,
  session,
  t,
  formatDateTime,
  formatMeters,
  formatLocationMatchLabel,
}) {
  if (!session) return null;

  const effectiveLocationStatus = getEffectiveLocationMatchStatus(session);
  const locationStatusClass = effectiveLocationStatus;
  const distanceSummary =
    formatDistanceSummary(session.distance_from_outlet_meters) ||
    t("admin.sessions.na");
  const accuracyLevel = getAccuracyLevel(session.check_in_accuracy_meters);
  const accuracyValue = formatAccuracyValue(session.check_in_accuracy_meters);
  const accuracyLevelClass = accuracyLevel ? `is-${accuracyLevel}` : "";

  return (
    <DetailDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t("admin.sessions.locationAudit.drawerTitle")}
    >
      <div className="admin-session-audit-drawer">
        <section className={`admin-session-audit-focus is-${locationStatusClass}`}>
          <div className="admin-session-audit-focus-head">
            <span
              className={`admin-sessions-location-badge is-${locationStatusClass}`}
            >
              {formatLocationMatchLabel(effectiveLocationStatus)}
            </span>
            <strong>{distanceSummary}</strong>
          </div>
          <h4>{session.promoter_name || t("admin.sessions.na")}</h4>
          <p>{t("admin.sessions.locationAudit.drawerTitle")}</p>
          <div className="admin-session-audit-focus-outlet">
            <strong>{session.outlet_name || t("admin.sessions.na")}</strong>
            <span>{session.outlet_address || t("admin.sessions.na")}</span>
          </div>
        </section>

        <section className="admin-session-audit-section">
          <h5>{t("admin.sessions.locationAudit.section.session")}</h5>
          <div className="admin-session-audit-stack">
            <div className="admin-session-audit-row">
              <span>{t("admin.sessions.promoter")}</span>
              <strong>{session.promoter_name || t("admin.sessions.na")}</strong>
            </div>
            <div className="admin-session-audit-row">
              <span>{t("admin.sessions.checkIn")}</span>
              <strong>{formatDateTime(session.checked_in_at)}</strong>
            </div>
            <div className="admin-session-audit-row">
              <span>{t("admin.sessions.locationAudit.checkInAddress")}</span>
              <strong>{session.check_in_address || t("admin.sessions.na")}</strong>
            </div>
          </div>
        </section>

        <section className="admin-session-audit-section">
          <h5>{t("admin.sessions.locationAudit.section.audit")}</h5>
          <div className="admin-session-audit-stack">
            <div className="admin-session-audit-row">
              <span>{t("admin.sessions.locationAudit.distance")}</span>
              <strong>
                {formatMeters(session.distance_from_outlet_meters) ||
                  t("admin.sessions.na")}
              </strong>
            </div>
            <div className="admin-session-audit-row">
              <span>{t("admin.sessions.locationAudit.radius")}</span>
              <strong>
                {formatMeters(session.applied_radius_meters) ||
                  t("admin.sessions.na")}
              </strong>
            </div>
            <div className="admin-session-audit-row">
              <span>{t("admin.sessions.locationAudit.accuracy")}</span>
              <strong className="admin-session-audit-accuracy-group">
                <span>{accuracyValue || t("admin.sessions.na")}</span>
                {accuracyLevel ? (
                  <span className={`admin-session-audit-accuracy-badge ${accuracyLevelClass}`}>
                    {t(`admin.sessions.locationAudit.accuracyLevel.${accuracyLevel}`)}
                  </span>
                ) : null}
              </strong>
            </div>
            <div className="admin-session-audit-row is-subtle">
              <span>{t("admin.sessions.locationAudit.radiusApplied")}</span>
              <strong>{formatMeters(session.applied_radius_meters) || t("admin.sessions.na")}</strong>
            </div>
          </div>
        </section>
      </div>
    </DetailDrawer>
  );
}
